/**
 * Market Intelligence Page
 *
 * Advanced carrier search and filtering for market analysis, prospecting,
 * and business intelligence. Search by location, fleet size, insurance status,
 * safety ratings, and more.
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search, MapPin, Truck, Shield, FileText, Download,
  Building2, AlertCircle, Calendar, Filter, TrendingUp
} from 'lucide-react';
import { Navbar } from '../components/shared/Navbar';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { logger } from '../utils/logger';
import api from '../services/api';

interface SearchFilters {
  // Geographic filters
  state?: string;
  city?: string;

  // Fleet size filters
  fleetSizeMin?: number;
  fleetSizeMax?: number;
  fleetSizeExact?: number;

  // Insurance filters
  insuranceExpiringDays?: number;
  insuranceType?: 'bipd' | 'cargo' | 'bond' | 'any';
  insuranceCompany?: string;
  sharedPoliciesOnly?: boolean;

  // Safety rating filters
  safetyRating?: 'satisfactory' | 'conditional' | 'unsatisfactory' | 'not-rated' | 'any';

  // Authority filters
  authorityType?: 'carrier' | 'broker' | 'dual' | 'any';
  authorityStatus?: 'active' | 'inactive' | 'any';

  // Carrier classification (cross-filter: authority + operation scope)
  carrierClassification?: string;

  // Operating status
  outOfService?: boolean;

  // Results pagination
  page?: number;
  limit?: number;
}

interface SearchResult {
  dot_number: number;
  legal_name: string;
  dba_name?: string;
  phy_city?: string;
  phy_state?: string;
  power_units?: number;
  truck_units?: number;
  safety_rating?: string;
  safety_rating_date?: string;
  common_authority?: string;
  broker_authority?: string;
  // BIPD Insurance fields
  bipd_expiration_date?: string;
  bipd_insurance_on_file?: number;
  bipd_insurance_required?: number;
  bipd_insurance_company?: string;
  bipd_policy_number?: string;
  bipd_posted_date?: string;
  bipd_cancellation_date?: string;
  bipd_policy_effective_date?: string;
  // Cargo Insurance fields
  cargo_expiration_date?: string;
  cargo_insurance_on_file?: number;
  cargo_insurance_required?: number;
  cargo_insurance_company?: string;
  cargo_policy_number?: string;
  cargo_posted_date?: string;
  cargo_cancellation_date?: string;
  cargo_policy_effective_date?: string;
  // Bond Insurance fields
  bond_expiration_date?: string;
  bond_insurance_on_file?: number;
  bond_insurance_required?: number;
  bond_insurance_company?: string;
  bond_policy_number?: string;
  bond_posted_date?: string;
  bond_cancellation_date?: string;
  // Contact
  phone?: string;
  email_address?: string;
  status_code?: string;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  totalPages: number;
  filters: SearchFilters;
}

export const MarketIntelligence: React.FC = () => {
  const location = useLocation();
  const [filters, setFilters] = useState<SearchFilters>({
    limit: 50,
    page: 1,
    authorityType: 'any',
    authorityStatus: 'any',  // Changed from 'active' - was too restrictive (filtered out 84% of carriers)
    safetyRating: 'any',
    insuranceType: 'any'
  });

  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read URL parameters on mount and auto-search if carrierClassification is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const classification = params.get('carrierClassification');

    if (classification) {
      // Auto-trigger search with classification filter
      const updatedFilters = { ...filters, carrierClassification: classification };
      setFilters(updatedFilters);
      handleSearch(updatedFilters);
    }
  }, [location.search]);

  const handleSearch = async (searchFilters?: SearchFilters | React.MouseEvent) => {
    try {
      setLoading(true);
      setError(null);

      // Ignore if called with event object, use current filters instead
      const filtersToUse = (searchFilters && typeof searchFilters === 'object' && !('nativeEvent' in searchFilters))
        ? searchFilters as SearchFilters
        : filters;

      // Build query string - only include valid filter keys
      const validKeys = ['state', 'city', 'fleetSizeMin', 'fleetSizeMax', 'fleetSizeExact',
                         'insuranceExpiringDays', 'insuranceType', 'insuranceCompany', 'sharedPoliciesOnly',
                         'safetyRating', 'authorityType', 'authorityStatus', 'outOfService', 'carrierClassification', 'page', 'limit'];

      const queryParams = new URLSearchParams();
      Object.entries(filtersToUse).forEach(([key, value]) => {
        if (validKeys.includes(key) && value !== undefined && value !== '' && value !== 'any' && value !== false) {
          queryParams.append(key, String(value));
        }
      });

      // Use API service (axios) instead of fetch() to include authentication header
      console.log('🔍 Market Intelligence Search:', queryParams.toString());
      const response = await api.get(`/carriers/search?${queryParams}`);
      console.log('✅ Search Response:', response.data);
      console.log('📊 Results count:', response.data.results?.length || 0);
      setResults(response.data);  // Fixed: axios already unwraps to response.data
    } catch (err) {
      logger.error('Market intelligence search failed', err);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      if (!results || results.results.length === 0) {
        alert('No results to export. Please run a search first.');
        return;
      }

      // Generate CSV
      const headers = ['DOT Number', 'Legal Name', 'DBA Name', 'City', 'State', 'Fleet Size',
                      'Safety Rating', 'Authority Type',
                      'BIPD Insurance Company', 'BIPD Policy Number', 'BIPD Posted Date', 'BIPD Cancellation Date',
                      'Cargo Company', 'Cargo Policy', 'Cargo Posted Date', 'Cargo Cancellation Date',
                      'Bond Company', 'Bond Policy', 'Bond Posted Date', 'Bond Cancellation Date',
                      'Phone', 'Email'];

      const rows = results.results.map(c => [
        c.dot_number,
        c.legal_name || '',
        c.dba_name || '',
        c.phy_city || '',
        c.phy_state || '',
        c.power_units || c.truck_units || 0,
        c.safety_rating || 'Not Rated',
        c.common_authority === 'A' ? 'Carrier' : c.broker_authority === 'A' ? 'Broker' : 'N/A',
        c.bipd_insurance_company || '',
        c.bipd_policy_number || '',
        c.bipd_posted_date ? new Date(c.bipd_posted_date).toLocaleDateString() : '',
        c.bipd_cancellation_date ? new Date(c.bipd_cancellation_date).toLocaleDateString() : '',
        c.cargo_insurance_company || '',
        c.cargo_policy_number || '',
        c.cargo_posted_date ? new Date(c.cargo_posted_date).toLocaleDateString() : '',
        c.cargo_cancellation_date ? new Date(c.cargo_cancellation_date).toLocaleDateString() : '',
        c.bond_insurance_company || '',
        c.bond_policy_number || '',
        c.bond_posted_date ? new Date(c.bond_posted_date).toLocaleDateString() : '',
        c.bond_cancellation_date ? new Date(c.bond_cancellation_date).toLocaleDateString() : '',
        c.phone || '',
        c.email_address || ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `market-intelligence-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      logger.info('Export completed', { format, rows: rows.length });
    } catch (err) {
      logger.error('Export failed', err);
      alert('Export failed. Please try again.');
    }
  };

  const handleReset = () => {
    setFilters({
      limit: 50,
      page: 1,
      authorityType: 'any',
      authorityStatus: 'active',
      safetyRating: 'any',
      insuranceType: 'any'
    });
    setResults(null);
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container-spacious" style={{ paddingTop: 'var(--space-2xl)', paddingBottom: 'var(--space-3xl)' }}>

        {/* Page Header */}
        <div className="card-modern" style={{
          marginBottom: 'var(--space-2xl)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: 'var(--space-2xl)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
            <TrendingUp size={32} />
            <h1 style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>
              Market Intelligence
            </h1>
          </div>
          <p style={{ fontSize: '1.125rem', opacity: 0.95, margin: 0 }}>
            Advanced carrier search and market analysis tools. Find carriers by location, fleet size,
            insurance status, safety ratings, and more.
          </p>
        </div>

        {/* Search Filters */}
        <div className="card-modern" style={{ marginBottom: 'var(--space-2xl)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)', paddingBottom: 'var(--space-md)', borderBottom: '2px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Filter size={24} color="#667eea" />
              <h2 className="heading-2" style={{ margin: 0 }}>Advanced Search Filters</h2>
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
              Configure filters below and click Search
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>

            {/* Geographic Filters */}
            <div style={{ padding: 'var(--space-sm)', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <MapPin size={16} color="#667eea" />
                Location
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <select
                  className="input-modern"
                  value={filters.state || ''}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value || undefined })}
                >
                  <option value="">All States</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="input-modern"
                  placeholder="City (optional)"
                  value={filters.city || ''}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value || undefined })}
                />
              </div>
            </div>

            {/* Fleet Size Filters */}
            <div style={{ padding: 'var(--space-sm)', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Truck size={16} color="#667eea" />
                Fleet Size
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <input
                  type="number"
                  className="input-modern"
                  placeholder="Exact units (optional)"
                  min="0"
                  value={filters.fleetSizeExact || ''}
                  onChange={(e) => setFilters({ ...filters, fleetSizeExact: e.target.value ? parseInt(e.target.value) : undefined, fleetSizeMin: undefined, fleetSizeMax: undefined })}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-sm)' }}>
                  <input
                    type="number"
                    className="input-modern"
                    placeholder="Min units"
                    min="0"
                    value={filters.fleetSizeMin || ''}
                    onChange={(e) => setFilters({ ...filters, fleetSizeMin: e.target.value ? parseInt(e.target.value) : undefined, fleetSizeExact: undefined })}
                    disabled={!!filters.fleetSizeExact}
                  />
                  <input
                    type="number"
                    className="input-modern"
                    placeholder="Max units"
                    min="0"
                    value={filters.fleetSizeMax || ''}
                    onChange={(e) => setFilters({ ...filters, fleetSizeMax: e.target.value ? parseInt(e.target.value) : undefined, fleetSizeExact: undefined })}
                    disabled={!!filters.fleetSizeExact}
                  />
                </div>
              </div>
            </div>

            {/* Insurance Filters */}
            <div style={{ padding: 'var(--space-sm)', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Shield size={16} color="#667eea" />
                Insurance
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <select
                  className="input-modern"
                  value={filters.insuranceType || 'any'}
                  onChange={(e) => setFilters({ ...filters, insuranceType: e.target.value as any })}
                >
                  <option value="any">Any Insurance Type</option>
                  <option value="bipd">BIPD Insurance</option>
                  <option value="cargo">Cargo Insurance</option>
                  <option value="bond">Broker Bond</option>
                </select>
                <input
                  type="text"
                  className="input-modern"
                  placeholder="Insurance company (e.g., Progressive)"
                  value={filters.insuranceCompany || ''}
                  onChange={(e) => setFilters({ ...filters, insuranceCompany: e.target.value || undefined })}
                />
                <input
                  type="number"
                  className="input-modern"
                  placeholder="Expiring in X days"
                  min="0"
                  max="365"
                  value={filters.insuranceExpiringDays || ''}
                  onChange={(e) => setFilters({ ...filters, insuranceExpiringDays: e.target.value ? parseInt(e.target.value) : undefined })}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={filters.sharedPoliciesOnly || false}
                    onChange={(e) => setFilters({ ...filters, sharedPoliciesOnly: e.target.checked || undefined })}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: '0.875rem' }}>Shared policies only (2+ carriers)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', background: '#fef3c7', borderRadius: '4px', border: '1px solid #f59e0b' }}>
                  <input
                    type="checkbox"
                    checked={filters.carrierClassification === 'insurance_pending_cancellation'}
                    onChange={(e) => setFilters({
                      ...filters,
                      carrierClassification: e.target.checked ? 'insurance_pending_cancellation' : undefined
                    })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e' }}>
                    ⚠️ Posted for Cancellation
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px', background: '#fee2e2', borderRadius: '4px', border: '1px solid #dc2626' }}>
                  <input
                    type="checkbox"
                    checked={filters.carrierClassification === 'insurance_cancelling_30days'}
                    onChange={(e) => setFilters({
                      ...filters,
                      carrierClassification: e.target.checked ? 'insurance_cancelling_30days' : undefined
                    })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>
                    🚨 Cancelling in 30 Days
                  </span>
                </label>
              </div>
            </div>

            {/* Safety Rating Filters */}
            <div style={{ padding: 'var(--space-sm)', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <FileText size={16} color="#667eea" />
                Safety Rating
              </h3>
              <select
                className="input-modern"
                value={filters.safetyRating || 'any'}
                onChange={(e) => setFilters({ ...filters, safetyRating: e.target.value as any })}
                style={{ width: '100%' }}
              >
                <option value="any">All Ratings</option>
                <option value="satisfactory">Satisfactory</option>
                <option value="conditional">Conditional</option>
                <option value="unsatisfactory">Unsatisfactory</option>
                <option value="not-rated">Not Rated</option>
              </select>
            </div>

            {/* Authority Filters */}
            <div style={{ padding: 'var(--space-sm)', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '6px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Building2 size={16} color="#667eea" />
                Authority
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <select
                  className="input-modern"
                  value={filters.authorityType || 'any'}
                  onChange={(e) => setFilters({ ...filters, authorityType: e.target.value as any })}
                >
                  <option value="any">All Authority Types</option>
                  <option value="carrier">Carrier Only</option>
                  <option value="broker">Broker Only</option>
                  <option value="dual">Dual Authority</option>
                </select>
                <select
                  className="input-modern"
                  value={filters.authorityStatus || 'active'}
                  onChange={(e) => setFilters({ ...filters, authorityStatus: e.target.value as any })}
                >
                  <option value="any">Any Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive/Revoked</option>
                </select>
              </div>
            </div>

            {/* Additional Filters */}
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <AlertCircle size={18} color="#667eea" />
                Status
              </h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filters.outOfService || false}
                  onChange={(e) => setFilters({ ...filters, outOfService: e.target.checked || undefined })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Out of Service Only</span>
              </label>
            </div>

          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)', flexWrap: 'wrap' }}>
            <button
              onClick={handleSearch}
              className="btn-primary"
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}
            >
              <Search size={18} />
              {loading ? 'Searching...' : 'Search Carriers'}
            </button>
            <button
              onClick={handleReset}
              className="btn-secondary"
              disabled={loading}
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="card-modern" style={{
            marginBottom: 'var(--space-2xl)',
            padding: 'var(--space-lg)',
            background: '#fee',
            border: '1px solid #fcc'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', color: '#c00' }}>
              <AlertCircle size={20} />
              <strong>Error:</strong> {error}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
            <LoadingSpinner />
          </div>
        )}

        {/* Search Results */}
        {!loading && results && (
          <div className="card-modern">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
              <div>
                <h2 className="heading-2" style={{ margin: 0, marginBottom: 'var(--space-xs)' }}>
                  Search Results
                </h2>
                <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>
                  Found {results.total.toLocaleString()} carriers matching your criteria
                  (Page {results.page} of {results.totalPages})
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => handleExport('csv')}
                  className="btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                >
                  <Download size={16} />
                  Export CSV
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  className="btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                >
                  <Download size={16} />
                  Export Excel
                </button>
              </div>
            </div>

            {/* Results Table */}
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>DOT Number</th>
                    <th>Carrier Name</th>
                    <th>Location</th>
                    <th>Fleet Size</th>
                    <th>Safety Rating</th>
                    <th>Authority</th>
                    <th>Insurance Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((carrier) => (
                    <tr key={carrier.dot_number}>
                      <td>
                        <a href={`/carrier/${carrier.dot_number}`} style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}>
                          {carrier.dot_number}
                        </a>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{carrier.legal_name}</div>
                        {carrier.dba_name && (
                          <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                            DBA: {carrier.dba_name}
                          </div>
                        )}
                      </td>
                      <td>
                        {carrier.phy_city && carrier.phy_state
                          ? `${carrier.phy_city}, ${carrier.phy_state}`
                          : carrier.phy_state || 'N/A'}
                      </td>
                      <td>
                        {carrier.power_units || carrier.truck_units || 0} units
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: carrier.safety_rating === 'SATISFACTORY' ? '#d1fae5' :
                                      carrier.safety_rating === 'CONDITIONAL' ? '#fef3c7' :
                                      carrier.safety_rating === 'UNSATISFACTORY' ? '#fee2e2' : '#f3f4f6',
                          color: carrier.safety_rating === 'SATISFACTORY' ? '#065f46' :
                                 carrier.safety_rating === 'CONDITIONAL' ? '#92400e' :
                                 carrier.safety_rating === 'UNSATISFACTORY' ? '#991b1b' : '#6b7280'
                        }}>
                          {carrier.safety_rating || 'Not Rated'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem' }}>
                          {carrier.common_authority === 'A' && <div>✓ Carrier</div>}
                          {carrier.broker_authority === 'A' && <div>✓ Broker</div>}
                          {!carrier.common_authority && !carrier.broker_authority && 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                          {/* BIPD Insurance */}
                          {(carrier.bipd_insurance_on_file && Number(carrier.bipd_insurance_on_file) > 0) || carrier.bipd_expiration_date || carrier.bipd_cancellation_date ? (
                            <div style={{ marginBottom: '4px' }}>
                              <div>
                                <strong style={{ color: carrier.bipd_cancellation_date ? '#dc2626' : '#059669' }}>
                                  {carrier.bipd_cancellation_date ? '⚠️ BIPD' : '✓ BIPD'}
                                </strong>
                                {carrier.bipd_insurance_on_file && (
                                  <span style={{ color: 'var(--color-gray-500)', marginLeft: '4px', fontSize: '0.8rem' }}>
                                    (${Number(carrier.bipd_insurance_on_file).toLocaleString()}K)
                                  </span>
                                )}
                                {carrier.bipd_insurance_company && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginLeft: '6px' }}>
                                    - {carrier.bipd_insurance_company}
                                  </span>
                                )}
                              </div>
                              {carrier.bipd_cancellation_date && (
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', marginTop: '2px' }}>
                                  🚨 Cancels: {new Date(carrier.bipd_cancellation_date).toLocaleDateString()}
                                  {carrier.bipd_policy_effective_date && carrier.bipd_posted_date &&
                                   new Date(carrier.bipd_policy_effective_date).getTime() !== new Date(carrier.bipd_posted_date).getTime() && (
                                    <span style={{ color: 'var(--color-gray-500)', fontWeight: 400, marginLeft: '8px' }}>
                                      | Posted: {new Date(carrier.bipd_policy_effective_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : null}

                          {/* Cargo Insurance */}
                          {(carrier.cargo_insurance_on_file && Number(carrier.cargo_insurance_on_file) > 0) || carrier.cargo_expiration_date || carrier.cargo_cancellation_date ? (
                            <div style={{ marginBottom: '4px' }}>
                              <div>
                                <strong style={{ color: carrier.cargo_cancellation_date ? '#dc2626' : '#059669' }}>
                                  {carrier.cargo_cancellation_date ? '⚠️ Cargo' : '✓ Cargo'}
                                </strong>
                                {carrier.cargo_insurance_on_file && (
                                  <span style={{ color: 'var(--color-gray-500)', marginLeft: '4px', fontSize: '0.8rem' }}>
                                    (${Number(carrier.cargo_insurance_on_file).toLocaleString()}K)
                                  </span>
                                )}
                                {carrier.cargo_insurance_company && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginLeft: '6px' }}>
                                    - {carrier.cargo_insurance_company}
                                  </span>
                                )}
                              </div>
                              {carrier.cargo_cancellation_date && (
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', marginTop: '2px' }}>
                                  🚨 Cancels: {new Date(carrier.cargo_cancellation_date).toLocaleDateString()}
                                  {carrier.cargo_policy_effective_date && carrier.cargo_posted_date &&
                                   new Date(carrier.cargo_policy_effective_date).getTime() !== new Date(carrier.cargo_posted_date).getTime() && (
                                    <span style={{ color: 'var(--color-gray-500)', fontWeight: 400, marginLeft: '8px' }}>
                                      | Posted: {new Date(carrier.cargo_policy_effective_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : null}

                          {/* Bond */}
                          {(carrier.bond_insurance_on_file && Number(carrier.bond_insurance_on_file) > 0) || carrier.bond_expiration_date ? (
                            <div>
                              <strong style={{ color: '#059669' }}>✓ Bond</strong>
                              {carrier.bond_expiration_date && (
                                <span style={{ color: 'var(--color-gray-600)', marginLeft: '4px' }}>
                                  Exp: {new Date(carrier.bond_expiration_date).toLocaleDateString()}
                                </span>
                              )}
                              {!carrier.bond_expiration_date && carrier.bond_insurance_on_file && (
                                <span style={{ color: 'var(--color-gray-500)', marginLeft: '4px', fontSize: '0.8rem' }}>
                                  (${Number(carrier.bond_insurance_on_file).toLocaleString()}K on file)
                                </span>
                              )}
                            </div>
                          ) : null}

                          {/* No Insurance */}
                          {!carrier.bipd_insurance_on_file && !carrier.cargo_insurance_on_file && !carrier.bond_insurance_on_file &&
                           !carrier.bipd_expiration_date && !carrier.cargo_expiration_date && !carrier.bond_expiration_date && (
                            <span style={{ color: 'var(--color-gray-400)' }}>No insurance on file</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {results.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>
                <button
                  onClick={() => {
                    const newFilters = { ...filters, page: (filters.page || 1) - 1 };
                    setFilters(newFilters);
                    handleSearch(newFilters);
                  }}
                  disabled={filters.page === 1 || loading}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span style={{ padding: 'var(--space-sm) var(--space-md)', display: 'flex', alignItems: 'center' }}>
                  Page {results.page} of {results.totalPages}
                </span>
                <button
                  onClick={() => {
                    const newFilters = { ...filters, page: (filters.page || 1) + 1 };
                    setFilters(newFilters);
                    handleSearch(newFilters);
                  }}
                  disabled={filters.page === results.totalPages || loading}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* No Results */}
        {!loading && results && results.total === 0 && (
          <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
            <AlertCircle size={48} color="#9ca3af" style={{ margin: '0 auto var(--space-md)' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
              No carriers found
            </h3>
            <p style={{ color: 'var(--color-gray-600)' }}>
              Try adjusting your search filters to find more results.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MarketIntelligence;
